@if (!panelOpen()) {
  @if (prefs().launcherMinimized) {
    <button
      type="button"
      class="help-tab"
      data-tour="help-launcher"
      (click)="open()"
      [attr.aria-label]="'HELP.OPEN' | translate">
      <i class="pi pi-question-circle" aria-hidden="true"></i>
    </button>
  } @else {
    <div class="help-launcher-wrap">
      <button
        #launcher
        type="button"
        class="help-launcher"
        [class.help-launcher--pulse]="pulse()"
        data-tour="help-launcher"
        aria-haspopup="dialog"
        (click)="open()">
        <i class="pi pi-question-circle" aria-hidden="true"></i>
        <span>{{ 'HELP.LAUNCHER' | translate }}</span>
      </button>
      <button
        type="button"
        class="help-launcher-min"
        (click)="minimize()"
        [attr.aria-label]="'HELP.MINIMIZE' | translate"
        [attr.title]="'HELP.MINIMIZE' | translate">
        <i class="pi pi-angle-right" aria-hidden="true"></i>
      </button>
    </div>
  }
}

@if (panelOpen()) {
  <div class="help-scrim" (click)="close()" aria-hidden="true"></div>
  <aside
    class="help-panel"
    role="dialog"
    aria-modal="true"
    aria-labelledby="help-panel-title"
    cdkTrapFocus
    (keydown.escape)="close()">
    <header class="help-panel__head">
      <div class="help-panel__bar">
        @if (view().kind !== 'home' && !searching()) {
          <button type="button" class="help-icon-btn" (click)="back()" [attr.aria-label]="'HELP.BACK' | translate">
            <i class="pi pi-arrow-left" aria-hidden="true"></i>
          </button>
        }
        <h2 id="help-panel-title" class="help-panel__title">
          @if (searching()) {
            {{ 'HELP.RESULTS_TITLE' | translate }}
          } @else if (currentArticle(); as article) {
            {{ categoryLabel(article.category) }}
          } @else if (currentCategory(); as category) {
            {{ category.label[lang()] }}
          } @else {
            {{ 'HELP.TITLE' | translate }}
          }
        </h2>
        <button type="button" class="help-icon-btn" (click)="close()" [attr.aria-label]="'HELP.CLOSE' | translate">
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      </div>
      @if (view().kind === 'home' && !searching()) {
        <p class="help-panel__intro">{{ 'HELP.INTRO' | translate }}</p>
      }
    </header>

    <div class="help-search">
      <i class="pi pi-search help-search__icon" aria-hidden="true"></i>
      <input
        #searchInput
        type="search"
        class="help-search__input"
        [value]="query()"
        (input)="onQuery($event)"
        [attr.aria-label]="'HELP.SEARCH_LABEL' | translate"
        [placeholder]="'HELP.SEARCH_PLACEHOLDER' | translate" />
      @if (searching()) {
        <button type="button" class="help-search__clear" (click)="clearQuery()" [attr.aria-label]="'HELP.CLEAR_SEARCH' | translate">
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      }
    </div>

    <div class="help-panel__body">
      @if (loadError()) {
        <div class="help-empty" role="alert">
          <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
          <p>{{ 'HELP.LOAD_ERROR' | translate }}</p>
          <button type="button" class="help-btn help-btn--secondary" (click)="ensureContent()">{{ 'HELP.RETRY' | translate }}</button>
        </div>
      } @else if (!content()) {
        <div class="help-empty" aria-live="polite">
          <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
          <p>{{ 'HELP.LOADING' | translate }}</p>
        </div>
      } @else if (searching()) {
        @if (results().length) {
          <ul class="help-list" aria-live="polite">
            @for (article of results(); track article.id) {
              <li>
                <button type="button" class="help-item" (click)="openArticle(article.id)">
                  <span class="help-item__text">
                    <span class="help-item__title">{{ article.title[lang()] }}</span>
                    <span class="help-item__desc">{{ article.summary[lang()] }}</span>
                  </span>
                  <i class="pi pi-angle-right" aria-hidden="true"></i>
                </button>
              </li>
            }
          </ul>
        } @else {
          <div class="help-empty" aria-live="polite">
            <i class="pi pi-search" aria-hidden="true"></i>
            <p>{{ 'HELP.NO_RESULTS' | translate }}</p>
            <p class="help-empty__hint">{{ 'HELP.NO_RESULTS_HINT' | translate }}</p>
            <button type="button" class="help-btn help-btn--secondary" (click)="goTo('/dashboard/support')">
              <i class="pi pi-headphones" aria-hidden="true"></i>
              {{ 'HELP.CONTACT_SUPPORT' | translate }}
            </button>
          </div>
          <h3 class="help-section-title">{{ 'HELP.CATEGORIES' | translate }}</h3>
          <ul class="help-list">
            @for (category of categories(); track category.id) {
              <li>
                <button type="button" class="help-item" (click)="openCategory(category.id)">
                  <span class="help-item__icon"><i [class]="category.icon" aria-hidden="true"></i></span>
                  <span class="help-item__title">{{ category.label[lang()] }}</span>
                  <i class="pi pi-angle-right" aria-hidden="true"></i>
                </button>
              </li>
            }
          </ul>
        }
      } @else if (currentArticle(); as article) {
        <article class="help-article">
          <h3 class="help-article__title">{{ article.title[lang()] }}</h3>
          <p class="help-article__summary">{{ article.summary[lang()] }}</p>
          <ol class="help-article__steps">
            @for (step of article.steps[lang()]; track $index) {
              <li>{{ step }}</li>
            }
          </ol>
          @if (article.tip) {
            <p class="help-article__tip">
              <i class="pi pi-lightbulb" aria-hidden="true"></i>
              <span>{{ article.tip[lang()] }}</span>
            </p>
          }
          <div class="help-article__actions">
            @if (articleTour(); as tour) {
              <button type="button" class="help-btn help-btn--primary" (click)="startTour(tour.id, true)">
                <i class="pi pi-play" aria-hidden="true"></i>
                {{ 'HELP.SHOW_ME' | translate }}
              </button>
            }
            @if (article.route && article.routeLabel) {
              <button type="button" class="help-btn help-btn--secondary" (click)="goTo(article.route)">
                {{ article.routeLabel[lang()] }}
              </button>
            }
          </div>
        </article>
        @if (relatedArticles().length) {
          <h3 class="help-section-title">{{ 'HELP.RELATED' | translate }}</h3>
          <ul class="help-list">
            @for (related of relatedArticles(); track related.id) {
              <li>
                <button type="button" class="help-item" (click)="openArticle(related.id)">
                  <span class="help-item__title">{{ related.title[lang()] }}</span>
                  <i class="pi pi-angle-right" aria-hidden="true"></i>
                </button>
              </li>
            }
          </ul>
        }
      } @else if (currentCategory()) {
        <ul class="help-list">
          @for (article of categoryArticles(); track article.id) {
            <li>
              <button type="button" class="help-item" (click)="openArticle(article.id)">
                <span class="help-item__text">
                  <span class="help-item__title">{{ article.title[lang()] }}</span>
                  <span class="help-item__desc">{{ article.summary[lang()] }}</span>
                </span>
                <i class="pi pi-angle-right" aria-hidden="true"></i>
              </button>
            </li>
          }
        </ul>
      } @else {
        <h3 class="help-section-title">{{ 'HELP.CATEGORIES' | translate }}</h3>
        <ul class="help-list">
          @for (category of categories(); track category.id) {
            <li>
              <button type="button" class="help-item" (click)="openCategory(category.id)">
                <span class="help-item__icon"><i [class]="category.icon" aria-hidden="true"></i></span>
                <span class="help-item__title">{{ category.label[lang()] }}</span>
                <i class="pi pi-angle-right" aria-hidden="true"></i>
              </button>
            </li>
          }
        </ul>

        @if (tours().length) {
          <h3 class="help-section-title">{{ 'HELP.TOURS' | translate }}</h3>
          <ul class="help-list">
            @for (tour of tours(); track tour.id) {
              <li class="help-tour">
                <div class="help-tour__text">
                  <span class="help-item__title">{{ tour.title[lang()] }}</span>
                  <span class="help-item__desc">{{ tour.summary[lang()] }}</span>
                </div>
                <div class="help-tour__actions">
                  @switch (tourStatus(tour)) {
                    @case ('paused') {
                      <button type="button" class="help-btn help-btn--primary help-btn--sm" (click)="startTour(tour.id, false)">
                        {{ 'HELP.TOUR_CONTINUE' | translate }}
                      </button>
                      <button type="button" class="help-btn help-btn--ghost help-btn--sm" (click)="startTour(tour.id, true)">
                        {{ 'HELP.TOUR_RESTART' | translate }}
                      </button>
                    }
                    @case ('done') {
                      <span class="help-tour__badge"><i class="pi pi-check" aria-hidden="true"></i>{{ 'HELP.TOUR_DONE' | translate }}</span>
                      <button type="button" class="help-btn help-btn--secondary help-btn--sm" (click)="startTour(tour.id, true)">
                        {{ 'HELP.TOUR_REPEAT' | translate }}
                      </button>
                    }
                    @default {
                      <button type="button" class="help-btn help-btn--primary help-btn--sm" (click)="startTour(tour.id, true)">
                        {{ 'HELP.TOUR_START' | translate }}
                      </button>
                    }
                  }
                </div>
              </li>
            }
          </ul>
        }

        <div class="help-support">
          <i class="pi pi-headphones" aria-hidden="true"></i>
          <div class="help-support__text">
            <span class="help-item__title">{{ 'HELP.SUPPORT_TITLE' | translate }}</span>
            <span class="help-item__desc">{{ 'HELP.SUPPORT_DESC' | translate }}</span>
          </div>
          <button type="button" class="help-btn help-btn--secondary help-btn--sm" (click)="goTo('/dashboard/support')">
            {{ 'HELP.SUPPORT_CTA' | translate }}
          </button>
        </div>
      }
    </div>
  </aside>
}

<app-tour-overlay />
